<template>
  <v-container>
    <v-snackbar
      class="snackbar-top"
      v-model="notification.show"
      :top="true"
      :right="true"
      :color="notification.color"
    >
      {{ notification.message }}
      <v-btn
        fab small outlined
        @click="closeNotification"
      >
        <v-icon>clear</v-icon>
      </v-btn>
    </v-snackbar>

    <v-simple-table
      fixed-header
    >
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{$t('forms.name')}}</th>
            <th class="text-left">{{$t('forms.description')}}</th>
            <th class="text-center">{{$t('forms.component')}}</th>
            <th class="text-center">{{$t('forms.parent-category')}}</th>
            <th class="text-center">
              <v-btn @click="addCategory" fab small color="primary" :disabled="loading">
                <v-icon>add</v-icon>
              </v-btn>
              <v-btn @click="loadCategories" fab small color="primary" class="ml-2" :disabled="loading">
                <v-icon>refresh</v-icon>
              </v-btn>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="category in categories" :key="category.id">
            <td>{{ category.name }}</td>
            <td>{{ category.description }}</td>
            <td class="text-center">
              <v-chip>{{ category.component.name }}</v-chip>
            </td>
            <td class="text-center">
              <v-chip v-if="category.parent">{{ category.parent.name }}</v-chip>
              <v-chip v-else>{{ no_category.name }}</v-chip>
            </td>
            <td class="text-center">
              <v-btn @click="editCategory(category)" fab small :disabled="loading" color="blue accent-1" dark>
                <v-icon>edit</v-icon>
              </v-btn>
              <v-btn @click="removeCategory(category)" fab small :disabled="loading" class="ml-2" color="red accent-1" dark>
                <v-icon>delete</v-icon>
              </v-btn>
            </td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>

    <v-dialog width="700" v-model="dialog_form">
      <v-card>
        <v-card-title class="headline grey lighten-2" primary-title>
          {{current_category.id ? $t('forms.edit-record', {name: $t('forms.category')}) : $t('forms.create-record', {name: $t('forms.category')})}}
        </v-card-title>
        <v-card-text>
          <v-alert type="error" dismissible v-if="error" class="mt-5">{{error}}</v-alert>
          <v-alert type="success" dismissible v-if="notice" class="mt-5">{{notice}}</v-alert>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col cols="5" align="center" class="pt-2">
                <v-img :src="current_category.image" width="150" height="150"></v-img>
                <v-file-input
                  v-model="images.current"
                  :label="$t('forms.change-thumbnail')"
                  prepend-icon=""
                  chips
                  clearable
                  clear-icon="clear"
                  :rules="images.rule"
                  accept="image/jpeg"
                  @change="changeThumbnail"
                  @clear="changeThumbnail"
                  ></v-file-input>
                <p class="text-left">
                  {{$t('forms.only-images', {type: 'JPG/JPEG', ratio: '1:1', width: 150, height: 150, size: '200 Kb'})}}
                </p>
              </v-col>
              <v-col cols="7">
                <v-text-field
                  :label="$t('forms.name')"
                  type="text"
                  v-model="current_category.name"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.name')})]"
                  required
                ></v-text-field>

                <v-textarea
                  solo
                  :label="$t('forms.description')"
                  v-model="current_category.description"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.description')})]"
                  required
                ></v-textarea>

                <v-select
                  :label="$t('forms.component')"
                  v-model="current_category.component"
                  :items="components"
                  item-text="name"
                  item-value="id"
                  :rules="[v => !!v ||  this.$t('forms.is-required', {name: this.$t('forms.component')})]"
                  required
                  v-on:change="changeComponent"
                ></v-select>

                <v-select
                  :label="$t('forms.parent-category')"
                  v-model="current_category.parent"
                  :items="categories_component"
                  item-text="name"
                  item-value="id"
                  required
                ></v-select>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="saveCategory" :loading="loading" :disabled="loading">
            {{$t('forms.save')}}
            <template v-slot:loader>
              <span>{{$t('forms.loading-save')}}</span>
            </template>
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog persistent v-model="dialog_remove" max-width="500">
        <v-card>
            <v-card-title class="headline">{{$t('forms.sure-remove', {name: current_category.name})}}</v-card-title>
            <v-card-text>
                <p>{{$t('forms.sure-remove-description')}}</p>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn @click="cancelRemove">
                    {{ $t('forms.no-remove') }}
                </v-btn>
                <v-btn color="red accent-2" dark @click="confirmRemoveCategory">
                    {{ $t('forms.confirm-remove') }}
                </v-btn>
            </v-card-actions>
            <v-overlay
              :value="loading"
              >
              <v-progress-circular
                indeterminate
                color="primary"
              ></v-progress-circular>
            </v-overlay>
        </v-card>
   </v-dialog>

   <v-overlay :value="!loaded" opacity="1" color="white" light absolute>
      <v-progress-circular indeterminate size="64" color="primary"></v-progress-circular>
    </v-overlay>
  </v-container>
</template>

<style lang="scss">
  .v-snack--top {
      top: 5em !important;
  }
</style>

<script>
import * as service from '@/store/module/category'
import { getUrlImage, uploadImage, removeImage } from '@/store/storage'

export default {
  data() {
    return {
      categories: [],
      components: [],
      categories_component: [],
      no_category: {id: null, 'name': this.$t('forms.no-category')},
      valid: false,
      loaded: false,
      loading: false,
      error: null,
      notice: null,
      dialog_form: false,
      dialog_remove: false,
      current_category: null,
      notification: {
        color: 'gree',
        show: false,
        message: ''
      },
      images: {
        example: null,
        default: null,
        current: null,
        rule: [v => !v || v.size < 200000 || this.$t('errors.image-size', {size: '200 Kb'})]
      }
    }
  },
  created() {
    this.resetForm()
    this.loadCategories()
    this.loadComponents()

    getUrlImage(null, 'category', true).then(url => {
      this.images.example = url
    })
  },
  methods: {
    showNotification(success, message) {
      this.notification.message = message
      this.notification.color = success ? 'green' : 'red'
      this.notification.show = true
    },
    closeNotification() {
      this.notification.show = false
      this.notification.message = ''
    },
    loadComponents() {
      service.getDocumentsFromCollection('components').then(result => {
        this.components = result
        this.loaded = true
      })
    },
    loadCategories() {
      this.loaded = false
      service.getDocumentsFromCollection('categories', ['component', 'parent']).then(result => {
        this.categories = result
        this.loaded = true
      })
    },
    resetForm() {
      this.current_category = {
        id: null,
        name: '',
        description: '',
        parent: null,
        component: null,
        image: this.images.example
      }
      this.images.current = null
      if (this.$refs.form) {
        this.$refs.form.resetValidation()
      }
    },
    openFormDialog() {
      this.dialog_form = true
    },
    closeFormDialog() {
      this.dialog_form = false
    },
    openRemoveDialog() {
      this.dialog_remove = true
    },
    closeRemoveDialog() {
      this.dialog_remove = false
    },
    removeCategory(category) {
      this.current_category = category
      this.openRemoveDialog()
    },
    cancelRemove() {
      this.closeRemoveDialog()
      this.resetForm()
    },
    confirmRemoveCategory() {
      service.removeCategory(this.current_category.id)
        .then(async () => {
          await removeImage(this.current_category.id, 'category')
          this.showNotification(true, this.$t("forms.data-removed"))
          this.cancelRemove()
          this.loadCategories()
        })
        .catch(() => {
          this.showNotification(true, this.$t("errors.remove"))
        })
    },
    getUrlImage(image) {
      return getUrlImage(image, 'category', true)
    },
    changeComponent() {
      return (
        service.getCategoriesByComponent(this.current_category.component).then(result => {
          result.push({id: null, 'name': this.$t('forms.no-category')})
          this.categories_component = result
          return true
        })
      )
    },
    addCategory() {
      this.resetForm()
      this.openFormDialog()
    },
    async editCategory(category) {
      this.loading = true
      this.resetForm()
      this.openFormDialog()
      if (!category.image) {
        category.image = this.images.example
        category.image_loaded = true
      } else if (!category.image_loaded) {
        await getUrlImage(category.image, 'category', true).then(url => {
          category.image = url
          category.image_loaded = true
        })
      }

      this.current_category = {
        id: category.id,
        name: category.name,
        description: category.description,
        component: category.component.id,
        image: category.image
      }
      this.changeComponent().then(() => {
        this.categories_component = this.categories_component.filter(cat => cat.id !== category.id)
        this.categories_component.push(this.no_category)
        if (!category.parent) {
          category.parent = this.no_category
        }
        this.current_category.parent = category.parent.id
        this.loading = false
      })
    },
    changeThumbnail() {
      if (!this.images.current) {
        this.images.current = null
      }
    },
    saveCategory() {
      if (this.$refs.form.validate()) {
        this.loading = true
        this.notice = null
        this.error = null

        let component = service.getDocById('components', this.current_category.component)
        let parent = this.current_category.parent ? service.getDocById('categories', this.current_category.parent) : null
        let category = {
          id: this.current_category.id ? this.current_category.id : null,
          name: this.current_category.name,
          description: this.current_category.description,
          parent: parent,
          component: component
        }

        let action_save = this.current_category.id ? service.updateCategory : service.createCategory
        action_save(category)
        .then(async document => {
          // Upload image
          if (this.images.current) {
            uploadImage(document.id, 'category', this.images.current)
              .then(result => {
                let success = result.state === 'success'
                this.showNotification(success, success ? this.$t("results.image-uploaded") : this.$t("errors.upload-failed"))
                if (success) {
                  service.updateCategoryThumbnail(document.id)
                }
              })
          }
          // End save process
          this.showNotification(true, this.$t("forms.saved-record"))
          this.closeFormDialog()
          this.$refs.form.reset()
          this.loading = false
          this.loaded = false
          this.loadCategories()
        })
        .catch(error => {
          this.error = error.message
          this.loading = false
        })
      }
    }
  }
}
</script>
